/**
 * Token Utilities
 * Helper functions for JWT token handling
 */

/**
 * Decode JWT token without verification
 * @param {string} token - JWT token
 * @returns {object|null} Decoded token payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('[TokenUtils] Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  return Date.now() >= expiryTime;
};

/**
 * Get time remaining until token expires (in milliseconds)
 * @param {string} token - JWT token
 * @returns {number} Milliseconds until expiry (negative if already expired)
 */
export const getTokenExpiryTime = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return -1;
  }

  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  return expiryTime - Date.now();
};

/**
 * Check if token needs refresh based on threshold
 * @param {string} token - JWT token
 * @param {number} threshold - Time threshold in milliseconds (default: 5 minutes)
 * @returns {boolean} True if token should be refreshed
 */
export const shouldRefreshToken = (token, threshold = 5 * 60 * 1000) => {
  if (!token) return false;

  const timeRemaining = getTokenExpiryTime(token);

  // If already expired or expiring soon
  if (timeRemaining < 0) {
    return true; // Already expired
  }

  return timeRemaining <= threshold;
};

/**
 * Get token expiry date
 * @param {string} token - JWT token
 * @returns {Date|null} Expiry date or null if invalid
 */
export const getTokenExpiryDate = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  return new Date(decoded.exp * 1000);
};

/**
 * Format time remaining in human readable format
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} Formatted time string
 */
export const formatTimeRemaining = (milliseconds) => {
  if (milliseconds < 0) {
    return 'Expired';
  }

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

/**
 * Validate token structure
 * @param {string} token - JWT token
 * @returns {boolean} True if token has valid structure
 */
export const isValidTokenStructure = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const parts = token.split('.');
  return parts.length === 3;
};
